import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>

          <div className="prose prose-gray max-w-none">
            <p className="text-muted-foreground mb-8">
              Last updated: February 2026
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>
              <p>
                We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">Personal Information</h3>
              <ul className="list-disc pl-6">
                <li>Email address and password</li>
                <li>Display name and profile information</li>
                <li>User type (Creator, Developer, Player)</li>
                <li>Avatar image (optional)</li>
                <li>Platform information (for creators)</li>
                <li>Studio information (for developers)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Usage Information</h3>
              <ul className="list-disc pl-6">
                <li>Game preferences and wishlist</li>
                <li>Mission completion and coin balance</li>
                <li>Key requests and interactions</li>
                <li>Messages and communications</li>
                <li>Platform activity and engagement</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Technical Information</h3>
              <ul className="list-disc pl-6">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent</li>
                <li>Cookies and similar technologies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 mt-4">
                <li>Provide and maintain our services</li>
                <li>Process key requests and facilitate game distribution</li>
                <li>Track mission progress and award coins</li>
                <li>Send important updates and notifications</li>
                <li>Improve our platform and user experience</li>
                <li>Prevent fraud and ensure platform security</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">3. Information Sharing</h2>
              <p>We may share your information in the following circumstances:</p>

              <h3 className="text-xl font-semibold mb-3 mt-6">With Game Developers</h3>
              <ul className="list-disc pl-6">
                <li>Your email address and profile information when requesting keys</li>
                <li>Content creation metrics and engagement data</li>
                <li>Key request status and interaction history</li>
                <li>Platform activity related to their games</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">With Content Creators</h3>
              <ul className="list-disc pl-6">
                <li>Game information and release details</li>
                <li>Developer contact information (when approved)</li>
                <li>Community features and messaging</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Internal Analytics</h3>
              <ul className="list-disc pl-6">
                <li>Key request status and interactions for internal statistics</li>
                <li>Platform usage data for service improvement</li>
                <li>User engagement metrics for analytics purposes</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">With Service Providers</h3>
              <ul className="list-disc pl-6">
                <li>Hosting and infrastructure providers</li>
                <li>Analytics and monitoring services</li>
                <li>Email and communication services</li>
                <li>Payment processors (if applicable)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Legal Requirements</h3>
              <ul className="list-disc pl-6">
                <li>When required by law or legal process</li>
                <li>To protect our rights and prevent fraud</li>
                <li>In case of emergency or safety concerns</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">What We Do NOT Share</h3>
              <p>
                <strong>IndieKeys does NOT share the following information:</strong>
              </p>
              <ul className="list-disc pl-6 mt-4">
                <li>Game keys are <strong>never</strong> shared with external parties or sold</li>
                <li>Sensitive user information (passwords, financial data) is <strong>never</strong> shared</li>
                <li>Platform analytics remain <strong>confidential</strong> and are not sold to third parties</li>
                <li>User communication logs are <strong>never</strong> disclosed externally</li>
                <li>Steam data and gaming activity remain <strong>private</strong> and platform-internal</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. Data Security</h2>
              <p>
                We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes:
              </p>
              <ul className="list-disc pl-6 mt-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication</li>
                <li>Secure hosting infrastructure</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy. This includes:
              </p>
              <ul className="list-disc pl-6 mt-4">
                <li>Account information: Until account deletion</li>
                <li>Mission data: For analytics and platform improvement</li>
                <li>Communication logs: As required for support</li>
                <li>Legal compliance: As required by applicable laws</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Your Rights and Choices</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 mt-4">
                <li>Access and update your personal information</li>
                <li>Delete your account and associated data</li>
                <li>Opt out of non-essential communications</li>
                <li>Request data portability</li>
                <li>Object to certain data processing activities</li>
                <li>Withdraw consent where applicable</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Cookies and Tracking</h2>
              <p>
                We use cookies and similar technologies to enhance your experience, analyze usage patterns, and provide personalized content. You can control cookie settings through your browser preferences.
              </p>
              <div className="mt-4">
                <button
                  onClick={() => (window as any).tarteaucitron.userInterface.openPanel()}
                  className="text-primary hover:underline font-medium"
                >
                  Manage your cookie preferences
                </button>
              </div>
              <p className="mt-4">
                <strong>Analytics:</strong> We use Umami Analytics to understand website traffic and usage. Umami is a privacy-focused analytics tool that does not use cookies and does not track users across websites. All data collected is anonymized and compliant with GDPR.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">8. Third-Party Services</h2>
              <p>
                Our platform may integrate with third-party services (such as Steam for game data). These services have their own privacy policies, and we encourage you to review them.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data during such transfers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
              <p>
                Our services are not intended for children under 13. We do not knowingly collect personal information from children under 13. If we become aware of such collection, we will take steps to delete the information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us through our <a href="/contact" className="text-primary hover:underline">contact form</a>.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;